import { useFormik } from "formik";
import { Toast } from "primereact/toast";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { useFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { persologClassForm_PersologClassFragment$key } from "@relay/persologClassForm_PersologClassFragment.graphql";
import { createPersologClassFormSchema } from "./persolog-class-form.consts";
import { PERSOLOG_CLASS_FRAGMENT } from "./persolog-class-form.graphql";
import {
	PersologClassFormProps,
	PersologClassFormRef,
	PersologClassFormState,
} from "./persolog-class-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { ValidatedField } from "../../../../components/ValidatedField";

import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";

import { H2Span } from "../../../../themes/font-tags";
import { persologClasses } from "../persolog-element-form/persolog-element-form.consts";

export const PersologClassForm = forwardRef<PersologClassFormRef, PersologClassFormProps>(
	function PersologClassForm({ persologClassFragmentRef, onChange, onSubmit, classType }, ref) {
		const { t } = useTranslation(["common", "validation"]);
		const toast = useRef<Toast>(null);
		const existingClass = useFragment<persologClassForm_PersologClassFragment$key>(
			PERSOLOG_CLASS_FRAGMENT,
			persologClassFragmentRef ?? null,
		);

		const fallbackClass =
			persologClasses.find(
				(persologClass) => persologClass.classType === existingClass?.classType,
			) ?? persologClasses.find((persologClass) => persologClass.classType === classType)!;

		const initialValues: PersologClassFormState = {
			title: existingClass?.title ?? fallbackClass.title,
			text: existingClass?.text ?? fallbackClass.text,
			image: existingClass?.image ?? fallbackClass.image,
			classType: classType,
		};

		const formik = useFormik<PersologClassFormState>({
			initialValues,
			validationSchema: createPersologClassFormSchema(t),
			onSubmit: (values) => {
				onSubmit?.(values);
			},
		});

		useImperativeHandle(ref, () => ({
			persologClass: fallbackClass.title,
			values: formik.values,
			submit: formik.submitForm,
			validate: () =>
				formik.validateForm().then((errors) => Object.keys(errors).length === 0),
		}));

		return (
			<>
				<Toast ref={toast} />
				<H2Span>{fallbackClass.title}</H2Span>
				<ValidatedField<PersologClassFormState, string>
					name={"title"}
					label={t("general.title")}
					formikConfig={formik}
					onChange={onChange}
					required
					component={DefaultTextFieldComponent}
				/>
				<ValidatedField<PersologClassFormState, string>
					name={"text"}
					label={t("forms.text")}
					formikConfig={formik}
					onChange={onChange}
					required
					component={(p) => (
						<MemoizedRichTextEditor {...p} toolbar={RichTextEditorToolbar.WithLists} />
					)}
				/>
				<ValidatedField<PersologClassFormState, FileV2>
					name={"image"}
					label={t("forms.image")}
					component={({ fieldValue, updateField }) => {
						return (
							<FileSelectionField
								name={"image"}
								selectedFile={fieldValue}
								setSelectedFile={updateField}
								filterByFileTypes={["image/png", "image/jpg", "image/jpeg"]}
								canUploadFiles={true}
								canDeleteFiles={true}
							/>
						);
					}}
					formikConfig={formik}
				/>
			</>
		);
	},
);
